// ============================================================
// Page Sauvegardes Super Admin — Lot L22 (frontend)
// ============================================================
// Liste des sauvegardes par commerce, avec actions :
//   - Télécharger (.json.gz)
//   - Épingler / désépingler (rétention permanente)
//   - Supprimer (refusé si épinglée)
//   - Restaurer (avec motif + confirmation forte)
//   - Créer manuellement
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import superAdminApi from '../../services/superAdminApi';
import {
  Archive, Pin, PinOff, Download, RotateCcw, Trash2, Plus,
  AlertTriangle, Loader, Calendar,
} from 'lucide-react';
import toast from 'react-hot-toast';

const TYPE_LABELS = {
  auto_quotidien: { label: 'Quotidienne', color: 'bg-blue-500/15 text-blue-700 border-blue-200' },
  auto_mensuel:   { label: 'Mensuelle',   color: 'bg-purple-500/15 text-purple-700 border-purple-200' },
  manuel:         { label: 'Manuelle',    color: 'bg-amber-50 text-amber-700 border-amber-200' },
  pre_restauration: { label: 'Pré-restauration', color: 'bg-red-50 text-red-700 border-red-200' },
};

function formatTaille(octets) {
  if (!octets) return '—';
  const mo = octets / (1024 * 1024);
  if (mo >= 1) return `${mo.toFixed(2)} Mo`;
  return `${(octets / 1024).toFixed(1)} Ko`;
}

function formatDateHeure(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('fr-FR')} ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
}

// ────────────────────────────────────────────────────────────
// Modale de confirmation forte pour restauration
// ────────────────────────────────────────────────────────────
function ModalRestaurer({ sauvegarde, onClose, onSuccess }) {
  const [motif, setMotif] = useState('');
  const [confirmTexte, setConfirmTexte] = useState('');
  const [loading, setLoading] = useState(false);
  const phraseExigée = 'RESTAURER';

  const submit = async () => {
    if (confirmTexte !== phraseExigée) {
      toast.error(`Tape exactement "${phraseExigée}" pour confirmer`);
      return;
    }
    if (motif.trim().length < 10) {
      toast.error('Motif obligatoire (10 caractères minimum)');
      return;
    }
    setLoading(true);
    try {
      const { data } = await superAdminApi.post(
        `/superadmin/sauvegardes/${sauvegarde.id}/restaurer`,
        { motif: motif.trim() }
      );
      toast.success(`Restauration effectuée : ${data.nb_tables} tables, ${data.nb_lignes} lignes`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Échec de la restauration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-white border border-red-300 rounded-xl max-w-lg w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-50 border border-red-200 rounded-lg flex items-center justify-center">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
          <div>
            <h3 className="text-slate-900 font-semibold">Restauration irréversible</h3>
            <p className="text-xs text-slate-500">
              Une sauvegarde de sécurité sera créée avant l'opération.
            </p>
          </div>
        </div>

        <div className="bg-slate-50 rounded p-3 mb-4 text-sm space-y-1">
          <p className="text-slate-700">
            <span className="text-slate-400">Sauvegarde :</span>{' '}
            {formatDateHeure(sauvegarde.created_at)}
          </p>
          <p className="text-slate-700">
            <span className="text-slate-400">Commerce :</span>{' '}
            {sauvegarde.commerce_nom || sauvegarde.commerce_id?.substring(0, 8)}
          </p>
        </div>

        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Motif <span className="text-red-600">*</span>
            </label>
            <textarea
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              rows={2}
              placeholder="Ex: restauration suite à perte de données 04/06/2026"
              className="w-full bg-slate-100 border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Tape <span className="font-mono text-yellow-700">{phraseExigée}</span> pour confirmer
            </label>
            <input
              type="text"
              value={confirmTexte}
              onChange={(e) => setConfirmTexte(e.target.value)}
              className="w-full bg-slate-100 border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 font-mono"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={loading || confirmTexte !== phraseExigée || motif.trim().length < 10}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed
                       text-slate-900 text-sm font-medium rounded-lg flex items-center gap-2"
          >
            {loading ? <Loader size={14} className="animate-spin" /> : <RotateCcw size={14} />}
            Restaurer
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Page principale
// ────────────────────────────────────────────────────────────
export default function SuperAdminSauvegardes() {
  const [sauvegardes, setSauvegardes] = useState([]);
  const [commerces, setCommerces] = useState([]);
  const [filtreCommerce, setFiltreCommerce] = useState('');
  const [filtreType, setFiltreType] = useState('');
  const [loading, setLoading] = useState(false);
  const [aRestaurer, setARestaurer] = useState(null);
  const [creating, setCreating] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtreCommerce) params.commerce_id = filtreCommerce;
      if (filtreType)     params.type = filtreType;
      const { data } = await superAdminApi.get('/superadmin/sauvegardes', { params });
      setSauvegardes(data.sauvegardes || data || []);
    } catch (err) {
      toast.error('Échec du chargement');
    } finally {
      setLoading(false);
    }
  }, [filtreCommerce, filtreType]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    superAdminApi.get('/superadmin/commerces')
      .then((r) => setCommerces(r.data.commerces || r.data || []))
      .catch(() => {});
  }, []);

  const epingler = async (s) => {
    try {
      await superAdminApi.post(`/superadmin/sauvegardes/${s.id}/epingler`,
        { epinglee: !s.epinglee });
      toast.success(s.epinglee ? 'Désépinglée' : 'Épinglée — rétention permanente');
      charger();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Échec');
    }
  };

  const supprimer = async (s) => {
    if (s.epinglee) {
      toast.error('Désépingle avant de supprimer');
      return;
    }
    if (!window.confirm(`Supprimer définitivement cette sauvegarde du ${formatDateHeure(s.created_at)} ?`)) return;
    try {
      await superAdminApi.delete(`/superadmin/sauvegardes/${s.id}`);
      toast.success('Sauvegarde supprimée');
      charger();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Échec');
    }
  };

  const telecharger = async (s) => {
    try {
      const res = await superAdminApi.get(`/superadmin/sauvegardes/${s.id}/download`,
        { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `sauvegarde-${s.id.substring(0, 8)}.json.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Échec du téléchargement');
    }
  };

  const creerManuelle = async () => {
    if (!filtreCommerce) {
      toast.error('Choisis d\'abord un commerce dans le filtre');
      return;
    }
    setCreating(true);
    try {
      await superAdminApi.post('/superadmin/sauvegardes',
        { commerce_id: filtreCommerce });
      toast.success('Sauvegarde manuelle créée');
      charger();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Échec');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center">
            <Archive size={18} className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Sauvegardes</h1>
            <p className="text-xs text-slate-400">
              Quotidiennes (90j), mensuelles permanentes, restaurations encadrées
            </p>
          </div>
        </div>
        <button
          onClick={creerManuelle}
          disabled={creating || !filtreCommerce}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700
                     disabled:opacity-40 disabled:cursor-not-allowed text-slate-900 text-sm
                     font-medium rounded-lg"
        >
          {creating ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}
          Sauvegarde manuelle
        </button>
      </header>

      {/* Filtres */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-slate-500 mb-1">Commerce</label>
          <select
            value={filtreCommerce}
            onChange={(e) => setFiltreCommerce(e.target.value)}
            className="w-full bg-slate-100 border border-slate-300 rounded-lg px-2 py-1.5 text-sm text-slate-900"
          >
            <option value="">Tous</option>
            {commerces.map((c) => (
              <option key={c.id} value={c.id}>{c.nom}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs text-slate-500 mb-1">Type</label>
          <select
            value={filtreType}
            onChange={(e) => setFiltreType(e.target.value)}
            className="w-full bg-slate-100 border border-slate-300 rounded-lg px-2 py-1.5 text-sm text-slate-900"
          >
            <option value="">Tous</option>
            <option value="auto_quotidien">Quotidienne</option>
            <option value="auto_mensuel">Mensuelle</option>
            <option value="manuel">Manuelle</option>
            <option value="pre_restauration">Pré-restauration</option>
          </select>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs text-slate-500">
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Commerce</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Lignes</th>
                <th className="px-3 py-2 font-medium">Taille</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center text-slate-400 py-8">
                  <Loader size={20} className="animate-spin mx-auto" />
                </td></tr>
              ) : sauvegardes.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-slate-400 py-8">
                  Aucune sauvegarde
                </td></tr>
              ) : (
                sauvegardes.map((s) => {
                  const tlb = TYPE_LABELS[s.type] || { label: s.type, color: '' };
                  return (
                    <tr key={s.id} className="border-t border-slate-200 hover:bg-slate-50">
                      <td className="px-3 py-2 text-slate-700 flex items-center gap-2">
                        <Calendar size={12} className="text-slate-400" />
                        {formatDateHeure(s.created_at)}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {s.commerce_nom || s.commerce_id?.substring(0, 8)}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs border ${tlb.color}`}>
                          {tlb.label}
                        </span>
                        {s.epinglee && (
                          <span className="ml-1 inline-block px-2 py-0.5 rounded text-xs
                                          bg-yellow-50 text-yellow-700 border border-yellow-200">
                            <Pin size={10} className="inline mr-0.5" /> Épinglée
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-500 tabular-nums">
                        {(s.nb_lignes_total || 0).toLocaleString('fr-FR')}
                      </td>
                      <td className="px-3 py-2 text-slate-500 tabular-nums">
                        {formatTaille(s.taille_octets)}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => telecharger(s)}
                            title="Télécharger"
                            className="p-1.5 text-slate-500 hover:bg-slate-200 hover:text-slate-900 rounded"
                          >
                            <Download size={14} />
                          </button>
                          <button
                            onClick={() => epingler(s)}
                            title={s.epinglee ? 'Désépingler' : 'Épingler (rétention permanente)'}
                            className="p-1.5 text-slate-500 hover:bg-slate-200 hover:text-yellow-700 rounded"
                          >
                            {s.epinglee ? <PinOff size={14} /> : <Pin size={14} />}
                          </button>
                          <button
                            onClick={() => setARestaurer(s)}
                            title="Restaurer"
                            className="p-1.5 text-slate-500 hover:bg-slate-200 hover:text-red-600 rounded"
                          >
                            <RotateCcw size={14} />
                          </button>
                          <button
                            onClick={() => supprimer(s)}
                            disabled={s.epinglee}
                            title={s.epinglee ? 'Désépingle d\'abord' : 'Supprimer'}
                            className="p-1.5 text-slate-500 hover:bg-slate-200 hover:text-red-600 rounded
                                       disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {aRestaurer && (
        <ModalRestaurer
          sauvegarde={aRestaurer}
          onClose={() => setARestaurer(null)}
          onSuccess={() => { setARestaurer(null); charger(); }}
        />
      )}
    </div>
  );
}
